import { switchUserProfile } from '@/api/backend/profile';
import { ProfileType } from '@/api/backend/types';
import { cn, getAvatarInitials, getLocalizedName } from '@/lib/utils';
import { queryClient } from '@/services/queryClient';
import { useAuthStore } from '@/store/auth';
import { Avatar, AvatarFallback, AvatarImage, DropdownMenuItem } from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const NavBarProfileItem = ({ profile }: { profile: ProfileType }) => {
  const { i18n } = useTranslation();
  const { activeProfile } = useAuthStore();
  const navigate = useNavigate();

  const { mutateAsync: handleProfileClick, isPending: isProfileClickPending } = useMutation({
    mutationFn: async (newActiveProfile: ProfileType) => {
      if (activeProfile?.id === newActiveProfile.id) return;

      await switchUserProfile(newActiveProfile.id);

      queryClient.clear();
      navigate({
        to: '/dashboard',
        reloadDocument: true,
      });
    },
  });

  return (
    <DropdownMenuItem
      className="px-4 py-2 rounded-sm"
      key={profile.id}
      onClick={() => handleProfileClick(profile)}
      onSelect={(e) => {
        e.preventDefault();
      }}
    >
      <Avatar>
        <AvatarImage src="/avatars/01.png" />
        <AvatarFallback>{getAvatarInitials(profile.nameEn)}</AvatarFallback>
      </Avatar>

      <p className={cn(activeProfile?.id === profile.id && 'font-bold')}>
        {getLocalizedName(profile?.nameEn, profile?.nameAr, i18n.language)}
      </p>

      {isProfileClickPending && <Loader2 className="h-5 w-5 ml-auto animate-spin" />}
      {activeProfile?.id === profile.id && <Check className="h-5 w-5 ml-auto" />}
    </DropdownMenuItem>
  );
};

export default NavBarProfileItem;
